import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CustomColorPicker } from './PropertiesPanel';
import type { EditorialPropertyContext } from './editorialBlockProperties';
import {
  MENU_PILL_BORDER_RADIUS,
  MENU_SQUARE_BORDER_RADIUS,
  type MenuButtonShape,
  type MenuButtonStyle,
  resolveMenuButtonShape,
} from '../utils/menuBlockUtils';

function MenuColorField({
  ctx,
  id,
  label,
  value,
  onChange,
}: {
  ctx: EditorialPropertyContext;
  id: string;
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  const {
    showColorPicker,
    setShowColorPicker,
    colorPickerRef,
    getEmailBackgroundColors,
    brandColors,
    showAdvancedPicker,
    setShowAdvancedPicker,
    setAdvancedPickerPosition,
  } = ctx;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowColorPicker(showColorPicker === id ? null : id)}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner" style={{ backgroundColor: value }} />
          <span className="text-sm text-slate-700 font-mono uppercase">{value}</span>
        </button>
        {showColorPicker === id && (
          <div ref={colorPickerRef} className="absolute z-[101] mt-1 left-0 right-0">
            <CustomColorPicker
              currentColor={value}
              onChange={onChange}
              pickerType="solid"
              emailColors={getEmailBackgroundColors()}
              brandColors={brandColors}
              showAdvancedPicker={showAdvancedPicker}
              setShowAdvancedPicker={setShowAdvancedPicker}
              setAdvancedPickerPosition={setAdvancedPickerPosition}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const BUTTON_STYLE_OPTIONS: { value: MenuButtonStyle; label: string; description: string }[] = [
  { value: 'filled', label: 'Filled', description: 'Solid background buttons' },
  { value: 'outline', label: 'Outline', description: 'Border only, transparent fill' },
  { value: 'ghost', label: 'Ghost', description: 'Subtle background, no border' },
  { value: 'text', label: 'Text only', description: 'Plain links, no button chrome' },
];

export function MenuPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange, handleStyleChange } = ctx;
  const links: { label?: string; url?: string }[] = Array.isArray(block.content.links) ? block.content.links : [];
  const buttonStyle = (block.styles?.buttonStyle as MenuButtonStyle) || 'filled';
  const buttonShape = resolveMenuButtonShape(block.styles || {});
  const spacingPx = parseInt(String(block.styles?.linkSpacing || '10px').replace('px', ''), 10);
  const safeSpacing = Number.isFinite(spacingPx) ? spacingPx : 10;
  const itemPad = block.styles?.itemPadding || '8px 14px';

  const updateLink = (index: number, patch: Partial<{ label: string; url: string }>) => {
    const next = links.map((link, i) => (i === index ? { ...link, ...patch } : link));
    handleContentChange('links', next);
  };

  const removeLink = (index: number) => {
    handleContentChange(
      'links',
      links.filter((_, i) => i !== index)
    );
  };

  const setButtonShape = (shape: MenuButtonShape) => {
    handleStyleChange('buttonShape', shape);
    handleStyleChange(
      'itemBorderRadius',
      shape === 'pill' ? MENU_PILL_BORDER_RADIUS : MENU_SQUARE_BORDER_RADIUS
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Navigation links</h4>
        <div className="space-y-3">
          {links.map((link, idx) => (
            <div
              key={idx}
              className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2 relative group hover:border-slate-300 transition-all"
            >
              <button
                type="button"
                onClick={() => removeLink(idx)}
                className="absolute top-2 right-2 text-slate-400 hover:text-red-500 hover:bg-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                title="Remove link"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div>
                <label className="block text-[11px] font-semibold tracking-wide text-slate-500 mb-1 uppercase">Label</label>
                <input
                  type="text"
                  value={link.label ?? ''}
                  onChange={(e) => updateLink(idx, { label: e.target.value })}
                  className="w-[calc(100%-24px)] px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:border-slate-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-wide text-slate-500 mb-1 uppercase">URL</label>
                <input
                  type="text"
                  value={link.url ?? ''}
                  onChange={(e) => updateLink(idx, { url: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:border-slate-400 outline-none"
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => handleContentChange('links', [...links, { label: 'New Link', url: '#' }])}
          className="w-full py-2.5 bg-white border-2 border-dashed border-slate-300 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-4 h-4" /> Add link
        </button>
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Button style</h4>
        <div className="grid grid-cols-2 gap-2">
          {BUTTON_STYLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleStyleChange('buttonStyle', opt.value)}
              className={`px-3 py-2.5 text-left rounded-lg border text-sm transition-colors ${
                buttonStyle === opt.value
                  ? 'border-slate-800 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="font-semibold block">{opt.label}</span>
              <span className={`text-[10px] mt-0.5 block ${buttonStyle === opt.value ? 'text-slate-300' : 'text-slate-500'}`}>
                {opt.description}
              </span>
            </button>
          ))}
        </div>

        <div className="pt-1">
          <label className="block text-sm font-medium text-slate-700 mb-2">Button shape</label>
          <div className="flex gap-2">
            {(
              [
                { id: 'square' as const, label: 'Square', previewRadius: '2px' },
                { id: 'pill' as const, label: 'Pill', previewRadius: '999px' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setButtonShape(opt.id)}
                className={`flex-1 flex flex-col items-center gap-2 py-3 px-2 rounded-lg border text-sm transition-colors ${
                  buttonShape === opt.id
                    ? 'border-slate-800 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                <span
                  className="block w-14 h-6 border"
                  style={{
                    borderRadius: opt.previewRadius,
                    borderColor: buttonShape === opt.id ? 'rgba(255,255,255,0.5)' : '#94A3B8',
                    backgroundColor: buttonShape === opt.id ? 'rgba(255,255,255,0.15)' : '#F1F5F9',
                  }}
                />
                <span className="font-semibold text-xs">{opt.label}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Works with every button style above (filled, outline, ghost, or text).
          </p>
        </div>
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Button colors</h4>
        <MenuColorField
          ctx={ctx}
          id="menu-item-text"
          label="Link text color"
          value={(block.styles?.itemTextColor as string) || (block.styles?.color as string) || '#334155'}
          onChange={(color) => {
            handleStyleChange('itemTextColor', color);
            handleStyleChange('color', color);
          }}
        />
        {(buttonStyle === 'filled' || buttonStyle === 'ghost') && (
          <MenuColorField
            ctx={ctx}
            id="menu-item-bg"
            label="Button background"
            value={(block.styles?.itemBackgroundColor as string) || '#F8FAFC'}
            onChange={(color) => handleStyleChange('itemBackgroundColor', color)}
          />
        )}
        {(buttonStyle === 'filled' || buttonStyle === 'outline') && (
          <MenuColorField
            ctx={ctx}
            id="menu-item-border"
            label="Button border color"
            value={(block.styles?.itemBorderColor as string) || '#CBD5E1'}
            onChange={(color) => handleStyleChange('itemBorderColor', color)}
          />
        )}
        <MenuColorField
          ctx={ctx}
          id="menu-container-border"
          label="Container border color"
          value={(block.styles?.containerBorderColor as string) || '#E2E8F0'}
          onChange={(color) => handleStyleChange('containerBorderColor', color)}
        />
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Typography & spacing</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Font size</label>
            <input
              type="text"
              value={(block.styles?.fontSize as string) || '14px'}
              onChange={(e) => handleStyleChange('fontSize', e.target.value)}
              className="w-full px-2 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Weight</label>
            <select
              value={(block.styles?.fontWeight as string) || '600'}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              className="w-full px-2 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
            >
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semibold</option>
              <option value="700">Bold</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between">
            <span>Link spacing</span>
            <span className="text-slate-500 font-normal">{safeSpacing}px</span>
          </label>
          <input
            type="range"
            min={4}
            max={32}
            value={safeSpacing}
            onChange={(e) => handleStyleChange('linkSpacing', `${e.target.value}px`)}
            className="v2-slider w-full h-1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Button padding</label>
          <input
            type="text"
            value={itemPad}
            onChange={(e) => handleStyleChange('itemPadding', e.target.value)}
            placeholder="8px 14px"
            className="w-full px-2 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Text transform</label>
          <select
            value={(block.styles?.textTransform as string) || 'none'}
            onChange={(e) => handleStyleChange('textTransform', e.target.value)}
            className="w-full px-2 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
          >
            <option value="none">None</option>
            <option value="uppercase">Uppercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Alignment</label>
          <div className="flex gap-2">
            {(['left', 'center', 'right'] as const).map((align) => (
              <button
                key={align}
                type="button"
                onClick={() => handleStyleChange('textAlign', align)}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg border capitalize ${
                  (block.styles?.textAlign || 'center') === align
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {align}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
